// Thin client for the POST-only API: returns `data` on success, throws ApiError otherwise.

export class ApiError extends Error {
  constructor(error, status) {
    super(error?.message || `Request failed (${status})`)
    this.code = error?.code || 'internal'
    this.details = error?.details || {}
    this.status = status
  }
}

// The browser's budget for request bodies still in flight when a page goes away.
const KEEPALIVE_BYTES = 60 * 1024

/**
 * POST /api/<path>; params go as JSON. `token` sends a Bearer header instead of relying on the cookie.
 * `keepalive` lets the request finish after the page is hidden or closed (a body over the browser's
 * keepalive budget goes as a plain request).
 */
export async function call(path, params = {}, { token, keepalive } = {}) {
  const envelope = await send(path, params, { token, keepalive })
  if (envelope.success) return envelope
  throw new ApiError(envelope.error, envelope.status)
}

/** Same as call() but resolves with the raw envelope for any status (used by the API explorer). */
export async function send(path, params = {}, { token, keepalive = false } = {}) {
  const headers = { 'content-type': 'application/json' }
  if (token) headers.authorization = `Bearer ${token}`
  const body = JSON.stringify(params)
  let response
  try {
    response = await fetch(`/api/${path.replace(/^\/+/, '')}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body,
      keepalive: keepalive && new Blob([body]).size < KEEPALIVE_BYTES,
    })
  } catch {
    return offline()
  }
  try {
    return await response.json()
  } catch {
    return {
      success: false,
      status: response.status,
      message: null,
      data: null,
      error: { code: 'internal', message: `Unexpected response (${response.status})`, details: {} },
      meta: {},
    }
  }
}

function offline() {
  return {
    success: false,
    status: 0,
    message: null,
    data: null,
    error: { code: 'offline', message: 'Cannot reach the server', details: {} },
    meta: {},
  }
}

/**
 * POST /api/<path> with JSON params, for uploads. XHR rather than fetch, because only XHR
 * reports upload progress: `onProgress(0..1)`. Resolves with `data`, rejects with ApiError;
 * `signal` aborts (code `aborted`).
 */
export function upload(path, params, { onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `/api/${path.replace(/^\/+/, '')}`)
    xhr.withCredentials = true
    xhr.setRequestHeader('content-type', 'application/json')
    xhr.responseType = 'json'
    xhr.upload.onprogress = (event) => event.lengthComputable && onProgress?.(event.loaded / event.total)
    xhr.onload = () => {
      const envelope = xhr.response
      if (envelope?.success) resolve(envelope.data)
      else reject(new ApiError(envelope?.error || { code: 'internal', message: `Upload failed (${xhr.status})` }, xhr.status))
    }
    xhr.onerror = () => reject(new ApiError(offline().error, 0))
    xhr.onabort = () => reject(new ApiError({ code: 'aborted', message: 'Upload canceled' }, 0))
    signal?.addEventListener('abort', () => xhr.abort())
    xhr.send(JSON.stringify(params))
  })
}

/** POST a raw body to `url` with XHR progress: resolves with `data`, rejects with ApiError. */
function postRaw(url, body, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.withCredentials = true
    xhr.setRequestHeader('content-type', 'application/octet-stream')
    xhr.responseType = 'json'
    xhr.upload.onprogress = (event) => event.lengthComputable && onProgress(event.loaded)
    xhr.onload = () => {
      const envelope = xhr.response
      if (envelope?.success) resolve(envelope.data)
      else reject(new ApiError(envelope?.error || { code: 'internal', message: `Upload failed (${xhr.status})` }, xhr.status))
    }
    xhr.onerror = () => reject(new ApiError(offline().error, 0))
    xhr.send(body)
  })
}

export const CHUNK_SIZE = 32 * 1024 * 1024

/**
 * Sends `file` from byte `from` to `url?offset=N` in chunks small enough for the edge's body limit.
 * The server answers with the bytes it holds, so a chunk lost on the way is sent again (three tries).
 * `onProgress(bytesSent)`.
 */
export async function sendFile(url, file, { from = 0, onProgress = () => {} } = {}) {
  let offset = from
  let tries = 0
  while (offset < file.size) {
    const chunk = file.slice(offset, Math.min(offset + CHUNK_SIZE, file.size))
    try {
      const result = await postRaw(`${url}?offset=${offset}`, chunk, (loaded) => onProgress(offset + loaded))
      offset = result.received
      tries = 0
    } catch (error) {
      if (error.status !== 0 || ++tries > 3) throw error
      await new Promise((resolve) => setTimeout(resolve, 1000 * tries))
    }
    onProgress(offset)
  }
  return offset
}

